import { MutableRefObject, useEffect, useRef } from 'react';

import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import lottieWeb, { AnimationItem } from 'lottie-web';

export type LottieRenderer = 'svg' | 'canvas' | 'html';

const useInitLottieWeb = ({
    json,
    loop,
    speed,
    initialSegment,
    direction,
    onComplete,
    renderer,
}: {
    json: any;
    loop: boolean;
    speed: number;
    direction: number;
    renderer: LottieRenderer;
    onComplete?: () => void;
    /**
     * [startFrame, endFrame]
     */
    initialSegment?: [number, number];
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<any>(null);

    useEffect(() => {
        if (containerRef.current) {
            animationRef.current = lottieWeb.loadAnimation({
                container: containerRef.current,
                renderer,
                loop,
                autoplay: false,
                ...(initialSegment && { initialSegment }),
                // Lottie-Web can technically mutate the animationData object, so we need to clone it
                animationData: cloneDeep(json),
            });
        }

        return () => {
            if (animationRef.current) {
                animationRef.current.destroy();
            }
        };
    }, [json, loop, initialSegment?.[0], initialSegment?.[1], renderer]);

    useEffect(() => {
        if (animationRef.current) {
            animationRef.current.setSpeed(speed);
            animationRef.current.setDirection(direction);
            animationRef.current.onComplete = onComplete;
        }
    }, [speed, direction]);

    return { animationRef, containerRef };
};

const usePlayLottieAnimation = ({
    startPlay,
    animationRef,
    onPlayStart,
    playbackKey,
}: {
    startPlay: boolean;
    animationRef: MutableRefObject<any>;
    onPlayStart?: (lottieWebAnimation?: AnimationItem) => void;
    playbackKey?: unknown;
}) => {
    useEffect(() => {
        if (!startPlay || !animationRef.current) {
            return;
        }

        const animation = animationRef.current as AnimationItem;
        let hasStarted = false;

        const playAnimation = () => {
            if (hasStarted) {
                return;
            }
            hasStarted = true;
            onPlayStart?.(animation);
            // Force playback from frame 0 to avoid Safari getting stuck on an initial frame.
            animation.goToAndPlay(0, true);
        };

        const safeRemoveListener = (eventName: 'DOMLoaded' | 'loaded_images') => {
            const callbacks = (animation as any)?._cbs;
            if (!callbacks?.[eventName]) {
                return;
            }
            animation.removeEventListener(eventName, playAnimation);
        };

        // Safari can miss playback when image assets are still initializing.
        // Start once via load events, with a timeout fallback for already-loaded instances.
        animation.addEventListener('DOMLoaded', playAnimation);
        animation.addEventListener('loaded_images', playAnimation);
        const timeoutId = window.setTimeout(playAnimation, 120);

        return () => {
            window.clearTimeout(timeoutId);
            safeRemoveListener('DOMLoaded');
            safeRemoveListener('loaded_images');
        };
    }, [startPlay, onPlayStart, playbackKey]);
};

function LottieGraphic({
    className,
    json,
    speed = 1,
    startPlay = false,
    onPlayStart,
    direction = 1,
    loop = false,
    renderer = 'svg',
    initialSegment,
    onComplete,
}: {
    className?: string;
    json: any;
    startPlay: boolean;
    onPlayStart?: (lottieWebAnimation?: AnimationItem) => void;
    speed?: number;
    direction?: number;
    loop?: boolean;
    renderer?: LottieRenderer;
    /**
     * [startFrame, endFrame]
     */
    initialSegment?: [number, number];
    onComplete?: () => void;
}) {
    const { animationRef, containerRef } = useInitLottieWeb({
        json,
        loop,
        speed,
        initialSegment,
        direction,
        onComplete,
        renderer,
    });

    usePlayLottieAnimation({
        animationRef,
        onPlayStart,
        startPlay,
        playbackKey: json,
    });

    return (
        <div
            ref={containerRef}
            className={clsx('graphic', 'lottie-graphic', className)}
            // Safari can incorrectly composite SVG filter/matte layers outside the animation bounds.
            // Isolating paint + clipping prevents the lottie surface from tinting the whole page.
            style={{ contain: 'paint', isolation: 'isolate', overflow: 'hidden' }}
            data-chromatic="ignore"
        />
    );
}

export default LottieGraphic;
